import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionStatus = ({ currentSubscription, onUpgrade, onManageRenewal }) => {
  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-CM', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const getDaysRemaining = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'expiring': return 'text-warning';
      case 'expired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'expiring': return 'AlertTriangle';
      case 'expired': return 'XCircle';
      default: return 'Clock';
    }
  };

  const daysRemaining = getDaysRemaining(currentSubscription?.expiryDate);
  const status = daysRemaining > 7 ? 'active' : daysRemaining > 0 ? 'expiring' : 'expired';

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Current Subscription</h2>
          <p className="text-muted-foreground">Manage your geographic visibility and premium features</p>
        </div>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full bg-muted/50 ${getStatusColor(status)}`}>
          <Icon name={getStatusIcon(status)} size={16} />
          <span className="text-sm font-medium capitalize">{status}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {/* Current Plan */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Icon name="Globe" size={20} className="text-primary" />
            <h3 className="font-medium text-foreground">Geographic Visibility</h3>
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">{currentSubscription?.visibilityScope}</p>
            <p className="text-sm text-muted-foreground">{formatAmount(currentSubscription?.monthlyPrice)}/month</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Users" size={14} />
            <span>Reach: ~{currentSubscription?.estimatedReach} users</span>
          </div>
        </div>

        {/* Expiry Information */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={20} className="text-primary" />
            <h3 className="font-medium text-foreground">Subscription Period</h3>
          </div>
          <div>
            <p className="text-lg font-semibold text-foreground">
              {daysRemaining > 0 ? `${daysRemaining} days left` : 'Expired'}
            </p>
            <p className="text-sm text-muted-foreground">
              Expires: {formatDate(currentSubscription?.expiryDate)}
            </p>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                daysRemaining > 15 ? 'bg-success' : 
                daysRemaining > 7 ? 'bg-warning' : 'bg-error'
              }`}
              style={{ width: `${Math.max(0, Math.min(100, (daysRemaining / 30) * 100))}%` }}
            />
          </div>
        </div>

        {/* Auto-Renewal Status */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Icon name="RotateCcw" size={20} className="text-primary" />
            <h3 className="font-medium text-foreground">Auto-Renewal</h3>
          </div>
          <div>
            <p className={`text-lg font-semibold ${currentSubscription?.autoRenewal ? 'text-success' : 'text-muted-foreground'}`}>
              {currentSubscription?.autoRenewal ? 'Enabled' : 'Disabled'}
            </p>
            <p className="text-sm text-muted-foreground">
              {currentSubscription?.autoRenewal 
                ? `Next charge: ${formatDate(currentSubscription?.nextChargeDate)}`
                : 'Manual renewal required'
              }
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onManageRenewal}
            iconName="Settings"
            iconPosition="left"
            iconSize={14}
          >
            Manage
          </Button>
        </div>
      </div>
      {/* Premium Features */}
      {currentSubscription?.premiumFeatures && currentSubscription?.premiumFeatures?.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="font-medium text-foreground mb-4 flex items-center">
            <Icon name="Crown" size={18} className="mr-2 text-accent" />
            Active Premium Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentSubscription?.premiumFeatures?.map((feature, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-accent/5 border border-accent/20 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon name={feature?.icon} size={16} className="text-accent" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{feature?.name}</p>
                    <p className="text-xs text-muted-foreground">{feature?.description}</p>
                  </div>
                </div>
                <span className="text-sm font-medium text-accent">{formatAmount(feature?.price)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-border">
        <Button
          variant="default"
          onClick={onUpgrade}
          iconName="ArrowUp"
          iconPosition="left"
          iconSize={16}
          className="flex-1"
        >
          Upgrade Plan
        </Button>
        <Button
          variant="outline"
          onClick={() => window.open(`https://wa.me/237${currentSubscription?.supportPhone}`, '_blank')}
          iconName="MessageCircle"
          iconPosition="left"
          iconSize={16}
          className="flex-1"
        >
          Contact Support
        </Button>
      </div>
      {/* Warning for Expiring Subscription */}
      {status === 'expiring' && (
        <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
            <div>
              <p className="font-medium text-warning">Subscription Expiring Soon</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your subscription will expire in {daysRemaining} days. Renew now to maintain your visibility and avoid interruption.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Expired Subscription Warning */}
      {status === 'expired' && (
        <div className="mt-4 p-4 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="XCircle" size={20} className="text-error mt-0.5" />
            <div>
              <p className="font-medium text-error">Subscription Expired</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your listings are no longer visible. Renew your subscription to restore visibility and continue selling.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;